/* ============================================================
   La Nuit — product-form.js
   Wires:
     - thumbnail gallery (click swaps main image)
     - variant radio picker -> updates form hidden id, price, availability, URL
     - add-to-cart form submit -> AJAX /cart/add.js -> dispatches cart:added (opens drawer)
   ============================================================ */

(function () {
  const section = document.querySelector('.main-product[data-section-id]');
  if (!section) return;

  /* ---------- Gallery ---------- */

  const thumbs = section.querySelectorAll('[data-thumb]');
  const mainImages = section.querySelectorAll('.main-product__media-image');

  thumbs.forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.mediaId;
      thumbs.forEach(t => t.classList.toggle('is-active', t === btn));
      mainImages.forEach(img => img.classList.toggle('is-active', img.dataset.mediaId === id));
    });
  });

  /* ---------- Variant picker ---------- */

  const form = section.querySelector('[data-product-form]');
  // The swatch labels in product-variant-picker.liquid also carry
  // [data-variant-id] (used for hover-preview). Scope to the form's
  // hidden id input so we don't pick up a <label> by accident.
  const variantInput = section.querySelector('input[data-variant-id][name="id"]');
  const priceContainer = section.querySelector('[data-product-price]');
  const addBtn = section.querySelector('[data-add-to-cart]');
  const addLabel = section.querySelector('[data-add-label]');
  const variantPicker = section.querySelector('[data-variant-picker]');

  // Inline product data (we'll read from the script tag inserted below)
  const productScript = document.getElementById(`ProductData-${section.dataset.sectionId}`);
  let product = null;
  try {
    product = productScript ? JSON.parse(productScript.textContent) : null;
  } catch (e) {}

  // Picker-specific variant data (intensity_caption, featured_media_id, inventory)
  const pickerDataScript = section.querySelector('[data-variant-picker-data]');
  let pickerData = null;
  try {
    pickerData = pickerDataScript ? JSON.parse(pickerDataScript.textContent) : null;
  } catch (e) {}

  const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const optionInputs = section.querySelectorAll('[data-variant-option]');

  function getSelectedOptions() {
    const values = [];
    section.querySelectorAll('[data-option-position]').forEach(input => {
      // Only consider radio inputs (labels also carry data-option-position)
      if (input.tagName !== 'INPUT') return;
      if (!input.checked) return;
      values[parseInt(input.dataset.optionPosition, 10) - 1] = input.value;
    });
    return values;
  }

  function findVariant(values) {
    if (!product) return null;
    // Single-variant products (only "Default Title") render NO option
    // pickers, so getSelectedOptions() returns []. Without this fallback,
    // the length check below fails (1 !== 0) and add-to-cart is wrongly
    // blocked with "combination not available". A product with exactly
    // one variant can never be a wrong combination — return it.
    if (!values.length) {
      return product.variants.length === 1 ? product.variants[0] : null;
    }
    return product.variants.find(v =>
      v.options.length === values.length &&
      v.options.every((opt, i) => opt === values[i])
    );
  }

  // Resolve the section's "Buy it now" button up-front so updateForVariant
  // can mirror its state with the add-to-cart button. Also resolve the
  // sticky-bar add button (mobile) and any optional Shopify-emitted
  // payment_button container so we can hide the express-checkout row
  // when the current option combination doesn't correspond to a real,
  // purchasable variant.
  const buyNowBtnEarly = section.querySelector('[data-buy-now]');
  const stickyAddEarly = section.querySelector('[data-sticky-add]');
  const stickyBuyNowEarly = section.querySelector('[data-sticky-buy-now]');
  const paymentButtonContainer = section.querySelector('.shopify-payment-button');

  // Outer wrapper we render around payment_button so we can hide the
  // express-checkout row entirely (Shopify's .shopify-payment-button
  // sits *inside* this wrapper). Hiding the wrapper guarantees the
  // express row vanishes even if Shopify replaces / re-renders its
  // own inner container.
  const dynamicCheckoutWrap = section.querySelector('[data-dynamic-checkout]');

  function setExpressDisabled(disabled, reason) {
    if (buyNowBtnEarly) {
      buyNowBtnEarly.disabled = disabled;
      buyNowBtnEarly.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      if (reason) buyNowBtnEarly.setAttribute('title', reason);
      else buyNowBtnEarly.removeAttribute('title');
    }
    if (stickyAddEarly) {
      stickyAddEarly.disabled = disabled;
    }
    if (stickyBuyNowEarly) {
      stickyBuyNowEarly.disabled = disabled;
    }
    // Shopify's dynamic-checkout container — hide entirely when the
    // current variant is unavailable, so Apple Pay / Shop Pay / etc.
    // can't be clicked through a stale variant id.
    if (paymentButtonContainer) {
      paymentButtonContainer.style.display = disabled ? 'none' : '';
      paymentButtonContainer.setAttribute('aria-hidden', disabled ? 'true' : 'false');
      paymentButtonContainer.style.pointerEvents = disabled ? 'none' : '';
    }
    if (dynamicCheckoutWrap) {
      if (disabled) {
        dynamicCheckoutWrap.setAttribute('hidden', '');
        dynamicCheckoutWrap.setAttribute('aria-hidden', 'true');
      } else {
        dynamicCheckoutWrap.removeAttribute('hidden');
        dynamicCheckoutWrap.setAttribute('aria-hidden', 'false');
      }
    }
  }

  // Debug flag — set window.LaNuitDebug = true in dev console to surface
  // variant-id mismatch warnings without polluting production logs.
  function debugWarn(msg, ctx) {
    if (typeof window !== 'undefined' && window.LaNuitDebug === true) {
      // eslint-disable-next-line no-console
      console.warn('[la-nuit/product-form]', msg, ctx || '');
    }
  }

  // Mirror products: the Liquid-rendered hidden id is the MASTER variant id,
  // not anything that appears in this product's variants[] array. Never let
  // syncVariantInput overwrite it with a local (façade) variant id — that
  // would bypass the entire mirror system and post the wrong SKU to cart.
  const isMirror = !!(form && form.dataset.mirror === 'true');
  const mirrorSourceTitle = (form && form.dataset.mirrorSourceTitle) || '';
  const mirrorSourceUrl = (form && form.dataset.mirrorSourceUrl) || '';

  // Tracks current variant price in cents for sticky bar total calculation
  let _stickyBasePriceCents = 0;
  const massagerOfferIds = ['8870094471326', '8870094569630', '8870094733470', '8870094864542', '8870094930078', '8870095028382', '8870095159454'];
  const massagerPrice = cents => product && massagerOfferIds.includes(String(product.id)) ? Math.round(cents * 0.75) : cents;

  // Single source of truth for syncing the form hidden `id` input.
  // Called from updateForVariant AND from form submit (defensive). If
  // the picker JS ever lags behind a radio change, this guarantees the
  // posted variant id matches the live selection (Bug 2).
  function syncVariantInput(variant) {
    if (isMirror) return; // never rewrite — Liquid wrote the master id
    if (!variantInput || !variant) return;
    const oldValue = variantInput.value;
    const newValue = String(variant.id);
    if (oldValue !== newValue) {
      debugWarn('variant input out of sync — rewriting', { old: oldValue, new: newValue });
      variantInput.value = newValue;
    }
    // Also keep any other [name="id"] inputs in the same form aligned
    // (sticky bar / quick-add can wire a second hidden input).
    if (form) {
      form.querySelectorAll('input[name="id"]').forEach(inp => {
        if (inp.value !== newValue) inp.value = newValue;
      });
    }
  }

  function updateForVariant(variant) {
    if (!variant) {
      if (addBtn) {
        addBtn.disabled = true;
        if (addLabel) addLabel.textContent = 'Unavailable';
      }
      // CRITICAL: also disable the custom "Buy it now" button and any
      // Shopify dynamic-checkout buttons. Without this, the user can
      // click Buy-it-now on a non-existent option combo and the click
      // posts the LAST-VALID variant id from the hidden input — i.e.
      // checkout proceeds with the wrong variant. See QA report.
      setExpressDisabled(true, 'This combination is not available');
      // Render a small inline notice once per section.
      let notice = section.querySelector('[data-variant-unavailable-notice]');
      if (!notice) {
        notice = document.createElement('p');
        notice.setAttribute('data-variant-unavailable-notice', '');
        notice.className = 'main-product__variant-unavailable';
        notice.setAttribute('role', 'status');
        notice.textContent = 'This combination is not available. Please choose another option.';
        const buyRow = section.querySelector('.main-product__buy-row');
        if (buyRow && buyRow.parentNode) {
          buyRow.parentNode.insertBefore(notice, buyRow);
        }
      }
      notice.hidden = false;
      return;
    }
    // Write the resolved variant id into the form's hidden field. This
    // is the canonical fix for Bug 2 — without it, the form submit
    // posts whatever id Liquid rendered (first-available variant) even
    // after the user picked a different one. syncVariantInput also
    // covers any secondary [name="id"] hidden inputs.
    syncVariantInput(variant);
    if (addBtn) {
      addBtn.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? (addBtn.dataset.defaultLabel || 'Add to cart') : 'Sold out';
    }
    // Mirror add-button availability on the express-checkout controls.
    setExpressDisabled(!variant.available, variant.available ? '' : 'Out of stock');
    const notice = section.querySelector('[data-variant-unavailable-notice]');
    if (notice) notice.hidden = true;
    if (priceContainer && variant.price_html) {
      priceContainer.innerHTML = variant.price_html;
    }
    // Keep sticky bar price and options in sync with selected variant
    _stickyBasePriceCents = massagerPrice(variant.price);
    updateStickyBar();
    updateStickyOptions(variant);
    // Update URL without reload
    if (variant.id && window.history.replaceState) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
    // Switch gallery to variant image if present
    if (variant.featured_media) {
      mainImages.forEach(img => img.classList.toggle('is-active', String(img.dataset.mediaId) === String(variant.featured_media.id)));
      thumbs.forEach(t => t.classList.toggle('is-active', String(t.dataset.mediaId) === String(variant.featured_media.id)));
    }
    // Broadcast a canonical variant:change event so other modules
    // (product-gallery, dimensions block, etc.) can react.
    document.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: {
        variantId: variant.id,
        variant: variant,
        sectionId: section.dataset.sectionId
      }
    }));
  }

  function updateSelectedValueLabels() {
    section.querySelectorAll('[data-selected-value]').forEach(el => {
      const pos = el.dataset.selectedValue;
      const checked = section.querySelector(`input[data-option-position="${pos}"]:checked`);
      if (checked) el.textContent = checked.value;
    });
  }

  /* ---------- Intensity caption sync ---------- */

  function updateIntensityCaption(variant) {
    if (!variant || !pickerData) return;
    const pd = pickerData.variants.find(v => v.id === variant.id);
    if (!pd) return;
    section.querySelectorAll('[data-caption][data-option-position]').forEach(el => {
      el.textContent = pd.intensity_caption || '';
    });
  }

  /* ---------- Conditional disable (variant-radios updateAvailability) ----------
     For each option's value, dim values that have NO compatible variant given
     the OTHER currently selected options. */

  function updateAvailability() {
    if (!product) return;
    const selected = getSelectedOptions();
    section.querySelectorAll('[data-swatch][data-option-position]').forEach(label => {
      const pos = parseInt(label.dataset.optionPosition, 10);
      const optIdx = pos - 1;
      const value = label.dataset.optionValue;
      // Find any variant matching this value at this option AND matching every other selected option.
      const compatible = product.variants.some(v => {
        if (v.options[optIdx] !== value) return false;
        return selected.every((s, i) => i === optIdx || s == null || v.options[i] === s);
      });
      if (compatible) {
        label.removeAttribute('data-disabled');
        label.removeAttribute('title');
      } else {
        label.setAttribute('data-disabled', 'true');
        const others = selected.filter((_, i) => i !== optIdx).filter(Boolean).join(' / ');
        label.setAttribute('title', others ? `Not available with ${others}` : 'Not available');
      }
    });
  }

  /* ---------- Low-stock dot sync on the *selected* swatch ----------
     Adds a `data-low-stock-current` to the selected label when the current
     selected variant qualifies, so the dot can re-render dynamically. */
  function updateLowStockOnSelected(variant) {
    if (!variant || !pickerData) return;
    const pd = pickerData.variants.find(v => v.id === variant.id);
    const isLow = pd && pd.inventory_management === 'shopify' && pd.inventory_policy === 'deny'
      && pd.inventory_quantity > 0 && pd.inventory_quantity < 4;
    // No-op: server-rendered dots already cover representative variants. We
    // still mark the picker so gallery sync agent / addon agent can react.
    if (variantPicker) {
      variantPicker.toggleAttribute('data-selected-low-stock', !!isLow);
      if (isLow) variantPicker.dataset.selectedLowStockQty = String(pd.inventory_quantity);
    }
  }

  /* ---------- Hover-preview gallery swap (desktop) ---------- */

  let hoverTimer = null;
  let lastPreviewMediaId = null;
  function dispatchPreview(mediaId) {
    if (!mediaId) return;
    document.dispatchEvent(new CustomEvent('gallery:preview', {
      bubbles: true,
      detail: { mediaId, source: 'variant-picker' }
    }));
  }
  function restorePreview() {
    document.dispatchEvent(new CustomEvent('gallery:preview-restore', {
      bubbles: true,
      detail: { source: 'variant-picker' }
    }));
  }
  if (!reducedMotion && variantPicker) {
    const swatchLabels = variantPicker.querySelectorAll('[data-swatch][data-variant-media]');
    swatchLabels.forEach(label => {
      label.addEventListener('mouseenter', () => {
        clearTimeout(hoverTimer);
        const mediaId = label.dataset.variantMedia;
        hoverTimer = setTimeout(() => {
          lastPreviewMediaId = mediaId;
          dispatchPreview(mediaId);
        }, 220);
      });
      label.addEventListener('mouseleave', () => {
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => {
          // If a click happened, the selection sync will have already moved the gallery.
          // We only restore if nothing else changed in the meantime.
          if (lastPreviewMediaId) {
            restorePreview();
            lastPreviewMediaId = null;
          }
        }, 600);
      });
    });
  }

  /* ---------- Scent popover toggle ---------- */

  section.querySelectorAll('[data-scent-popover-toggle]').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('aria-controls');
      const pop = id ? document.getElementById(id) : null;
      if (!pop) return;
      const isOpen = !pop.hasAttribute('hidden');
      if (isOpen) {
        pop.setAttribute('hidden', '');
        btn.setAttribute('aria-expanded', 'false');
      } else {
        pop.removeAttribute('hidden');
        btn.setAttribute('aria-expanded', 'true');
      }
    });
  });

  // Close popovers on outside click / Esc.
  document.addEventListener('click', (e) => {
    section.querySelectorAll('[data-scent-popover]').forEach(pop => {
      if (pop.hasAttribute('hidden')) return;
      const toggle = section.querySelector(`[aria-controls="${pop.id}"]`);
      if (pop.contains(e.target) || (toggle && toggle.contains(e.target))) return;
      pop.setAttribute('hidden', '');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    section.querySelectorAll('[data-scent-popover]').forEach(pop => {
      if (pop.hasAttribute('hidden')) return;
      pop.setAttribute('hidden', '');
      const toggle = section.querySelector(`[aria-controls="${pop.id}"]`);
      if (toggle) { toggle.setAttribute('aria-expanded', 'false'); toggle.focus(); }
    });
  });

  /* ---------- Size persistence (localStorage) ---------- */

  const SIZE_KEY = 'lanuit-preferred-size';
  function persistSizeIfRelevant(input) {
    const fs = input.closest('[data-option-role]');
    if (!fs || fs.dataset.optionRole !== 'size') return;
    try { localStorage.setItem(SIZE_KEY, input.value); } catch (e) {}
  }

  // Pre-select stored size on boot.
  (function preselectStoredSize() {
    let stored = null;
    try { stored = localStorage.getItem(SIZE_KEY); } catch (e) {}
    if (!stored) return;
    const sizeFs = section.querySelector('[data-option-role="size"]');
    if (!sizeFs) return;
    const match = sizeFs.querySelector(`input[value="${CSS.escape(stored)}"]:not([data-soldout])`);
    if (match && !match.checked) {
      match.checked = true;
      match.dispatchEvent(new Event('change', { bubbles: true }));
    }
  })();

  // Block clicks on sold-out chips entirely.
  section.querySelectorAll('[data-variant-option][data-soldout="true"]').forEach(input => {
    input.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (window.LaNuit && typeof window.LaNuit.toast === 'function') {
        const optName = input.closest('[data-option-name]')?.dataset.optionName || 'option';
        window.LaNuit.toast(`${input.value} is currently sold out.`, { variant: 'info', duration: 2400 });
      }
    });
  });

  optionInputs.forEach(input => {
    input.addEventListener('change', () => {
      const values = getSelectedOptions();
      const variant = findVariant(values);
      updateForVariant(variant);
      updateSelectedValueLabels();
      updateAvailability();
      updateIntensityCaption(variant);
      updateLowStockOnSelected(variant);
      persistSizeIfRelevant(input);
      // Clear any stale hover-preview state — the click move is canonical now.
      lastPreviewMediaId = null;
    });
  });

  updateSelectedValueLabels();
  updateAvailability();
  {
    const initialValues = getSelectedOptions();
    const initialVariant = findVariant(initialValues);
    updateIntensityCaption(initialVariant);
    updateLowStockOnSelected(initialVariant);
  }

  /* ---------- Personalization (gift note) ---------- */

  section.querySelectorAll('[data-personalization]').forEach(panel => {
    const textarea = panel.querySelector('[data-personalization-input]');
    const counter = panel.querySelector('[data-personalization-count]');
    const counterWrap = panel.querySelector('.main-product__personalization-count');
    if (!textarea || !counter) return;
    const max = parseInt(textarea.getAttribute('maxlength'), 10) || 140;
    const updateCount = () => {
      const n = textarea.value.length;
      counter.textContent = String(n);
      if (counterWrap) counterWrap.classList.toggle('is-near-limit', n >= max - 20);
    };
    textarea.addEventListener('input', updateCount);
    updateCount();
  });

  /* ---------- Add to cart ---------- */

  /* Collect all checked add-on variants. Returns array of {id, title, price}. */
  function getSelectedAddons() {
    const checks = section.querySelectorAll('[data-product-addons] [data-addon-variant-id]');
    const out = [];
    checks.forEach(input => {
      if (input.checked && !input.disabled) {
        out.push({
          id: Number(input.dataset.addonVariantId),
          title: input.dataset.addonTitle || '',
          price: Number(input.dataset.addonPrice) || 0
        });
      }
    });
    return out;
  }

  /* Returns the request body for /cart/add.js. Four modes:
     1. bundle  — component-bundle multi-item payload
     2. mirror  — main item posts the master variant + `_Source` line item
                  property recording the façade that drove the sale. The
                  cart-line snippet reads `_Source` and renders the façade
                  title instead of the master's.
     3. addons  — main item + selected addons (one of each, qty 1)
     4. single  — plain FormData submit of the form
     Bundle modes (two formats; v2 wins if present):
       - data-bundle-items='[{"id":N,"qty":2},...]' (v2 — per-component qty)
       - data-bundle-components="id1,id2,id3"      (legacy — implicit qty=1)
     Final per-item quantity = (parent bundle qty selector) × (per-component qty).
  */
  function buildCartPayload(formEl, qtyOverride) {
    const bundleItemsRaw = formEl.dataset.bundleItems;
    const bundleLegacy = formEl.dataset.bundleComponents;
    const qty = qtyOverride || parseInt(formEl.querySelector('[name="quantity"]')?.value, 10) || 1;
    if (bundleItemsRaw) {
      let items = [];
      try {
        const parsed = JSON.parse(bundleItemsRaw);
        if (Array.isArray(parsed)) {
          items = parsed
            .map(entry => ({
              id: Number(entry?.id),
              quantity: Math.max(1, Number(entry?.qty) || 1) * qty
            }))
            .filter(it => Number.isFinite(it.id) && it.id > 0);
        }
      } catch (e) {
        // Fall through to legacy parsing below
      }
      if (items.length > 0) {
        return {
          contentType: 'application/json',
          body: JSON.stringify({ items })
        };
      }
    }
    if (bundleLegacy) {
      const ids = bundleLegacy.split(',').map(s => s.trim()).filter(Boolean);
      return {
        contentType: 'application/json',
        body: JSON.stringify({ items: ids.map(id => ({ id: Number(id), quantity: qty })) })
      };
    }

    // Collect visible form properties (gift note, etc.) once — used by
    // both the mirror branch and the legacy addons branch.
    function collectFormProperties() {
      const out = {};
      const fd = new FormData(formEl);
      for (const [k, v] of fd.entries()) {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m && typeof v === 'string' && v.length > 0) {
          out[m[1]] = v;
        }
      }
      return out;
    }

    // Mirror branch — façade product posts the master variant id (already
    // in the hidden input from Liquid) with a `_Source` property so the
    // cart-line snippet can render the façade title back to the customer
    // and the merchant sees provenance on the order in admin.
    if (isMirror) {
      const mainVarId = Number(formEl.querySelector('input[data-variant-id][name="id"]')?.value);
      if (Number.isFinite(mainVarId) && mainVarId > 0) {
        const props = collectFormProperties();
        if (mirrorSourceTitle) props['_Source'] = mirrorSourceTitle;
        if (mirrorSourceUrl) props['_SourceUrl'] = mirrorSourceUrl;
        if (formEl.dataset.preorder === 'true') props['_Pre-order'] = 'Yes';
        const item = { id: mainVarId, quantity: qty };
        if (Object.keys(props).length > 0) item.properties = props;
        // Addons stay supported when the façade has add-ons enabled — same
        // pattern as the addons branch below, but the main item is the
        // master variant.
        const mirrorAddons = getSelectedAddons();
        const items = [item];
        mirrorAddons.forEach(a => items.push({
          id: a.id,
          quantity: 1,
          properties: { '_Add-on for': mirrorSourceTitle || (document.querySelector('.main-product__title')?.textContent.trim() || '') }
        }));
        return {
          contentType: 'application/json',
          body: JSON.stringify({ items })
        };
      }
    }

    const addons = getSelectedAddons();
    if (addons.length > 0) {
      const mainVarId = Number(formEl.querySelector('input[data-variant-id][name="id"]')?.value);
      const items = [
        {
          id: mainVarId,
          quantity: qty,
          properties: formEl.dataset.preorder === 'true'
            ? { '_Pre-order': 'Yes' }
            : undefined
        },
        ...addons.map(a => ({
          id: a.id,
          quantity: 1,
          properties: { '_Add-on for': document.querySelector('.main-product__title')?.textContent.trim() || '' }
        }))
      ];
      return {
        contentType: 'application/json',
        body: JSON.stringify({ items })
      };
    }
    return { contentType: null, body: new FormData(formEl) };
  }

  /* Live addon subtotal display */
  function updateAddonSubtotal() {
    const totalEl = section.querySelector('[data-addon-total]');
    const valueEl = section.querySelector('[data-addon-total-value]');
    if (!totalEl || !valueEl) return;
    const addons = getSelectedAddons();
    const sumCents = addons.reduce((s, a) => s + a.price, 0);
    if (addons.length === 0) {
      totalEl.hidden = true;
      return;
    }
    totalEl.hidden = false;
    const currency = window.LaNuit?.cartCurrency || 'AED';
    valueEl.textContent = formatMoney(sumCents, currency);
  }

  function formatMoney(cents, currency) {
    const amount = (cents / 100).toFixed(2);
    return `${currency} ${amount}`;
  }

  section.querySelectorAll('[data-product-addons] [data-addon-variant-id]').forEach(input => {
    input.addEventListener('change', () => { updateAddonSubtotal(); updateStickyBar(); });
  });
  updateAddonSubtotal();

  function updateStickyBar() {
    const priceEl = section.querySelector('[data-sticky-price]');
    if (!priceEl) return;
    const addonCents = getSelectedAddons().reduce((s, a) => s + a.price, 0);
    const totalCents = _stickyBasePriceCents + addonCents;
    const currency = window.LaNuit?.cartCurrency || 'AED';
    priceEl.textContent = formatMoney(totalCents, currency);
    const badge = section.querySelector('[data-sticky-addon-badge]');
    if (badge) {
      const count = getSelectedAddons().length;
      badge.hidden = count === 0;
      if (count > 0) badge.textContent = `+${count}`;
    }
  }

  function updateStickyOptions(variant) {
    const el = section.querySelector('[data-sticky-options]');
    if (!el || !variant) return;
    const opts = (variant.options || []).filter(o => o !== 'Default Title');
    el.textContent = opts.join(' · ');

    const swatchEl = section.querySelector('[data-sticky-swatch]');
    if (!swatchEl) return;

    // Find the first checked swatch or finish chip in the picker
    const checkedInput = section.querySelector('[data-swatch] input:checked');
    const label = checkedInput ? checkedInput.closest('[data-swatch]') : null;
    const fillEl = label ? label.querySelector('.variant-option__swatch, .variant-option__finish-fill') : null;

    if (fillEl) {
      const swatchColor = fillEl.style.getPropertyValue('--swatch');
      const bgImage = fillEl.style.backgroundImage;
      swatchEl.removeAttribute('style');
      if (bgImage && bgImage !== 'none' && bgImage !== '') {
        swatchEl.style.backgroundImage = bgImage;
      } else if (swatchColor) {
        swatchEl.style.setProperty('--swatch', swatchColor.trim());
        swatchEl.style.backgroundColor = 'var(--swatch)';
      }
      swatchEl.hidden = false;
    } else {
      swatchEl.hidden = true;
    }
  }

  /* ---------- Add-to-cart reconciliation ----------
     A POST to /cart/add.js can reach Shopify and be applied while the
     response never reaches the browser (a dropped connection, an edge
     error, a blocked response). The old code treated that as a plain
     failure, told the shopper it did not work, and left the button
     ready for another tap: every retry added another unit. A shopper
     ended up with six framed prints in the cart while the page kept
     saying the add had failed.

     So: remember the cart count before posting, and when the request
     looks like it failed, ask the cart what actually happened before
     saying anything. `null` means we could not tell, and the caller
     falls back to reporting the failure. */
  function readCartCount() {
    const el = document.querySelector('[data-cart-count]');
    if (!el) return null;
    const n = parseInt(el.textContent.trim(), 10);
    return Number.isFinite(n) ? n : null;
  }

  async function fetchCartCount() {
    try {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!r.ok) return null;
      const cart = await r.json();
      return typeof cart.item_count === 'number' ? cart.item_count : null;
    } catch (e) {
      return null;
    }
  }

  async function addDidLand(countBefore) {
    if (countBefore === null) return false;
    const after = await fetchCartCount();
    return after !== null && after > countBefore;
  }

  /* Recompute the add button's disabled state from live option state,
     never from whatever it was before the request. */
  function restoreAddButton() {
    if (!addBtn) return;
    const v = findVariant(getSelectedOptions());
    addBtn.disabled = !(v && v.available);
  }

  let addInFlight = false;

  form?.addEventListener('submit', async (e) => {
    e.preventDefault();

    // One add at a time. The button used to carry aria-busy only, so a
    // double tap on a touch screen queued a second POST behind the
    // first and quietly doubled the order.
    if (addInFlight) return;

    // Defensive: re-check live variant availability before posting.
    // The picker can call updateForVariant(null) which disables addBtn,
    // but a stale form submit (e.g. from sticky-bar dispatch) must
    // also be blocked here.
    const submitValues = getSelectedOptions();
    const submitVariant = findVariant(submitValues);

    // CRITICAL safety net for Bug 2: re-resolve the variant from live
    // radio state and re-write the form's hidden id input on every
    // submit. Without this, a stale id (e.g. the Liquid-rendered first
    // available variant) can be posted if any prior change-handler
    // race left the input behind. We do this *before* the availability
    // check so the toast-rejection path also reads the right state.
    if (submitVariant) {
      syncVariantInput(submitVariant);
    }

    if (!submitVariant || !submitVariant.available) {
      if (window.LaNuit && typeof window.LaNuit.toast === 'function') {
        window.LaNuit.toast(
          submitVariant ? 'That variant is sold out.' : 'This combination is not available. Please choose another option.',
          { variant: 'info', duration: 3000 }
        );
      }
      return;
    }

    if (!window.LaNuit?.Cart) return form.submit();

    addInFlight = true;
    addBtn?.setAttribute('aria-busy', 'true');
    if (addBtn) addBtn.disabled = true;
    const originalLabel = addLabel?.textContent;
    if (addLabel) addLabel.textContent = 'Adding…';
    const countBefore = readCartCount();

    const announceAdded = () => {
      const pdpTitle = document.querySelector('.main-product__title');
      const title = pdpTitle ? pdpTitle.textContent.trim() : null;
      const isPreorder = form.dataset.preorder === 'true';
      document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true, detail: { title, preorder: isPreorder } }));
      if (addLabel) addLabel.textContent = isPreorder ? 'Pre-ordered ✓' : 'Added ✓';
      setTimeout(() => { if (addLabel) addLabel.textContent = originalLabel; }, 1400);
    };

    try {
      const payload = buildCartPayload(form);
      const headers = { Accept: 'application/json' };
      if (payload.contentType) headers['Content-Type'] = payload.contentType;
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers,
        body: payload.body
      });
      if (!res.ok) {
        let reason = '';
        try {
          const data = await res.json();
          reason = data.description || data.message || '';
        } catch (e) {}
        const err = new Error(reason || 'Add to cart failed');
        err.userMessage = reason;
        err.status = res.status;
        throw err;
      }
      announceAdded();
    } catch (err) {
      console.error(err);
      // Did it land anyway? Shopify rejecting the item (sold out, a
      // quantity rule) is a real failure and says so; a lost response
      // is not, and the shopper must not be invited to add a second one.
      const landed = !err.userMessage && await addDidLand(countBefore);
      if (landed) {
        announceAdded();
      } else {
        const status = err && err.status ? ` (error ${err.status})` : '';
        const msg = err && err.userMessage
          ? err.userMessage
          : `We could not add this to your cart${status}. Please check your connection and try again.`;
        if (window.LaNuit && typeof window.LaNuit.toast === 'function') {
          window.LaNuit.toast(msg, { variant: 'error', duration: 4000 });
        }
        if (addLabel) addLabel.textContent = 'Try again';
        setTimeout(() => { if (addLabel) addLabel.textContent = originalLabel; }, 1800);
      }
    } finally {
      addInFlight = false;
      addBtn?.removeAttribute('aria-busy');
      restoreAddButton();
    }
  });

  // Reset add-to-cart label if the user returns via bfcache while
  // it was mid-transition ("Adding…" / "Added ✓").
  window.addEventListener('pageshow', () => {
    if (addLabel && addBtn?.dataset.defaultLabel) {
      const txt = addLabel.textContent.trim();
      if (txt === 'Adding…' || txt === 'Added ✓' || txt === 'Error') {
        addLabel.textContent = addBtn.dataset.defaultLabel;
        addBtn.removeAttribute('aria-busy');
      }
    }
  });

  if (addLabel) addBtn.dataset.defaultLabel = addLabel.textContent.trim();

  /* ---------- Buy it now ---------- */

  const buyNowBtn = section.querySelector('[data-buy-now]');
  if (buyNowBtn) {
    // Capture the original label once, so any state restore is reliable
    buyNowBtn.dataset.defaultLabel = buyNowBtn.textContent.trim();

    function resetBuyNow() {
      buyNowBtn.textContent = buyNowBtn.dataset.defaultLabel;
      buyNowBtn.classList.remove('is-processing');
      // Always recompute disabled from the live variant state — never
      // restore a stale "rendered" disabled flag (the user may have
      // changed options while the request was in flight).
      const currentValues = getSelectedOptions();
      const currentVariant = findVariant(currentValues);
      buyNowBtn.disabled = !(currentVariant && currentVariant.available);
    }

    buyNowBtn.addEventListener('click', async (e) => {
      // Re-check live variant state BEFORE any network call — defends
      // against rapid option changes between the disabled-state paint
      // and the click event firing.
      const liveValues = getSelectedOptions();
      const liveVariant = findVariant(liveValues);
      if (!liveVariant || !liveVariant.available) {
        e.preventDefault();
        e.stopPropagation();
        if (window.LaNuit && typeof window.LaNuit.toast === 'function') {
          window.LaNuit.toast('This combination is not available. Please choose another option.', { variant: 'info', duration: 3000 });
        }
        return;
      }
      // Re-sync the hidden id input — same Bug 2 defence as form submit.
      syncVariantInput(liveVariant);
      if (buyNowBtn.disabled) return;
      buyNowBtn.disabled = true;
      buyNowBtn.classList.add('is-processing');
      buyNowBtn.textContent = 'Processing…';
      const countBefore = readCartCount();
      try {
        const payload = buildCartPayload(form);
        const headers = { Accept: 'application/json' };
        if (payload.contentType) headers['Content-Type'] = payload.contentType;
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers,
          body: payload.body
        });
        if (!res.ok) throw new Error('Add failed');
        window.location.href = '/checkout';
      } catch (err) {
        // Same reconciliation as add-to-cart: if the item landed despite
        // the failed response, carry on to checkout rather than leaving
        // the shopper to press the button again and buy two.
        if (await addDidLand(countBefore)) {
          window.location.href = '/checkout';
          return;
        }
        buyNowBtn.textContent = 'Error, try again';
        setTimeout(resetBuyNow, 1600);
      }
    });

    // Restore the button if the user returns from checkout via back/close.
    // Both bfcache restores (pageshow with event.persisted) and a regular
    // browser back can leave the button stuck on "Processing…".
    window.addEventListener('pageshow', () => {
      if (buyNowBtn.classList.contains('is-processing')) resetBuyNow();
    });
    // Tab becomes visible again (covers Apple Pay sheet dismissal etc.)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && buyNowBtn.classList.contains('is-processing')) {
        resetBuyNow();
      }
    });
  }

  /* ---------- Estimated delivery date ---------- */

  // Inputs are server-rendered by snippets/product-shipping-policy.liquid, so
  // prep/transit/weekend are per-product and per-product-type. This used to be a
  // hardcoded "+2 business days, skip Fri/Sat" that silently contradicted the
  // "Ships within 24h." copy sitting next to it.
  const dateEl = section.querySelector('[data-delivery-date]');
  const estEl = section.querySelector('[data-delivery-estimate]');
  if (dateEl && estEl) {
    const int = (name, fallback) => {
      const v = parseInt(estEl.dataset[name], 10);
      return Number.isFinite(v) && v >= 0 ? v : fallback;
    };
    const prep = int('prepDays', 0);
    const transit = int('transitDays', 2);

    // Weekend as a comma list of JS day indices (0=Sun … 6=Sat). Empty = the
    // courier works every day. Default 6,0 = Sat+Sun.
    const weekend = new Set(
      (estEl.dataset.weekend ?? '6,0')
        .split(',')
        .map(d => parseInt(d, 10))
        .filter(Number.isFinite)
    );
    const isWorkday = d => !weekend.has(d.getDay());

    const target = new Date();
    // Past the cutoff, prep only starts on the next working day.
    const cutoff = parseInt(estEl.dataset.cutoffHour, 10);
    if (Number.isFinite(cutoff) && target.getHours() >= cutoff) target.setDate(target.getDate() + 1);

    let remaining = prep + transit;
    // Guard against a policy that marks every day non-working.
    let guard = 0;
    while (remaining > 0 && guard < 400) {
      target.setDate(target.getDate() + 1);
      guard++;
      if (isWorkday(target)) remaining--;
    }

    const opts = { weekday: 'short', day: 'numeric', month: 'short' };
    dateEl.textContent = target.toLocaleDateString('en-GB', opts);
  }

  /* ---------- Sticky mobile add-to-cart bar ---------- */

  const sticky = document.querySelector('[data-sticky-buy]');
  const stickyAdd = section.querySelector('[data-sticky-add]');
  const buyRow = section.querySelector('.main-product__buy-row');
  if (sticky && buyRow) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Show sticky bar when buy row is OUT of view (below or above viewport)
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', passed);
        sticky.setAttribute('aria-hidden', passed ? 'false' : 'true');
      });
    }, { threshold: 0, rootMargin: '0px 0px -10% 0px' });
    io.observe(buyRow);
  }
  if (stickyAdd && form) {
    stickyAdd.addEventListener('click', () => {
      if (stickyAdd.disabled) return;
      form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    });
  }

  // Wire sticky Buy Now to the main Buy Now button
  const stickyBuyNow = section.querySelector('[data-sticky-buy-now]');
  if (stickyBuyNow && buyNowBtn) {
    stickyBuyNow.addEventListener('click', () => {
      if (!stickyBuyNow.disabled) buyNowBtn.click();
    });
  }

  // Initialize sticky bar with the current variant on page load
  (() => {
    const initValues = getSelectedOptions();
    const initVariant = findVariant(initValues);
    if (initVariant) {
      _stickyBasePriceCents = massagerPrice(initVariant.price);
      updateStickyOptions(initVariant);
      updateStickyBar();
    }
  })();

  /* ---------- Gallery zoom lightbox ---------- */

  const zoomTrigger = section.querySelector('[data-gallery-zoom]');
  const zoom = document.getElementById('ProductZoom');
  if (zoomTrigger && zoom && zoom.parentElement !== document.body) {
    // Relocate to body so no ancestor (transform / contain / overflow) clips
    // the fixed-positioned backdrop. Custom-element state is preserved.
    document.body.appendChild(zoom);
  }
  if (zoomTrigger && zoom) {
    const stage = zoom.querySelector('[data-zoom-stage]');
    const images = stage ? stage.querySelectorAll('.product-zoom__image') : [];
    const counter = zoom.querySelector('[data-zoom-cur]');
    const prev = zoom.querySelector('[data-zoom-prev]');
    const next = zoom.querySelector('[data-zoom-next]');
    let activeIdx = 0;

    function setActive(idx) {
      if (!images.length) return;
      activeIdx = (idx + images.length) % images.length;
      images.forEach((img, i) => img.classList.toggle('is-active', i === activeIdx));
      if (counter) counter.textContent = String(activeIdx + 1);
    }
    function syncWithGallery() {
      // Match the currently active main image
      const activeMain = section.querySelector('.main-product__media-image.is-active');
      if (!activeMain) return setActive(0);
      const id = activeMain.dataset.mediaId;
      let idx = 0;
      images.forEach((img, i) => { if (img.dataset.mediaId === id) idx = i; });
      setActive(idx);
    }

    zoomTrigger.addEventListener('click', () => {
      syncWithGallery();
      zoom.hidden = false;
      // Custom <modal-dialog> picks up the hidden->visible transition and traps focus
      if (typeof zoom.show === 'function') zoom.show();
      else zoom.setAttribute('open', '');
    });

    // Click main image also opens zoom on desktop (touch already uses pinch)
    section.querySelectorAll('.main-product__media-image').forEach(img => {
      img.addEventListener('click', () => zoomTrigger.click());
    });

    prev?.addEventListener('click', () => setActive(activeIdx - 1));
    next?.addEventListener('click', () => setActive(activeIdx + 1));
    document.addEventListener('keydown', (e) => {
      if (zoom.hidden) return;
      if (e.key === 'ArrowLeft') setActive(activeIdx - 1);
      if (e.key === 'ArrowRight') setActive(activeIdx + 1);
    });
  }

  /* ---------- Dimensions block (live variant sync + unit toggle) ---------- */

  const dimensionsRoot = document.querySelector('[data-product-dimensions]');

  function fmtDim(value, units) {
    if (value == null) return '—';
    if (units === 'cm') return (value / 10).toFixed(1);
    if (units === 'in') return (value / 25.4).toFixed(2);
    return value;
  }

  function readDimMap(root) {
    const dataEl = root && root.querySelector('[data-dimensions-by-variant]');
    if (!dataEl) return null;
    try { return JSON.parse(dataEl.textContent); } catch (err) { return null; }
  }

  function currentDimEntry(root) {
    const map = readDimMap(root);
    if (!map) return null;
    // Prefer the live form variant id; fall back to first entry.
    const id = variantInput && variantInput.value;
    let entry = id ? map.find(d => String(d.id) === String(id)) : null;
    if (!entry && map.length > 0) entry = map[0];
    return entry;
  }

  function renderDimensions(root, entry, units) {
    if (!root || !entry) return;
    const unitLabel = units === 'cm' ? 'cm' : units === 'in' ? 'in' : 'mm';
    root.dataset.units = units;
    // Update each value + adjacent unit label
    const setText = (sel, val) => {
      const el = root.querySelector(sel);
      if (el) el.textContent = val;
    };
    setText('[data-dim-length]', fmtDim(entry.l, units));
    setText('[data-dim-width]',  fmtDim(entry.w, units));
    setText('[data-dim-height]', fmtDim(entry.h, units));
    setText('[data-dim-weight]', entry.weight == null ? '—' : entry.weight);
    setText('[data-dim-label]',  entry.label || '—');
    // Rewrite unit suffixes inline next to length/width/height values.
    ['length', 'width', 'height'].forEach((dim) => {
      const span = root.querySelector(`[data-dim-${dim}]`);
      if (!span) return;
      const dd = span.closest('dd');
      if (!dd) return;
      // dd contains: <span data-dim-x>VAL</span> UNIT
      // Replace the unit text node after the span.
      let node = span.nextSibling;
      while (node) {
        if (node.nodeType === Node.TEXT_NODE) {
          node.nodeValue = ' ' + unitLabel;
          return;
        }
        node = node.nextSibling;
      }
      // No text node — append one.
      dd.appendChild(document.createTextNode(' ' + unitLabel));
    });
  }

  // Variant change: re-render dimensions with current units.
  document.addEventListener('variant:change', () => {
    if (!dimensionsRoot) return;
    const entry = currentDimEntry(dimensionsRoot);
    if (!entry) return;
    const units = dimensionsRoot.dataset.units || 'mm';
    renderDimensions(dimensionsRoot, entry, units);
  });

  // Unit toggle clicks.
  if (dimensionsRoot) {
    const unitButtons = dimensionsRoot.querySelectorAll('[data-unit]');
    unitButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const units = btn.dataset.unit;
        unitButtons.forEach((b) => b.setAttribute('aria-checked', b === btn ? 'true' : 'false'));
        const entry = currentDimEntry(dimensionsRoot);
        renderDimensions(dimensionsRoot, entry, units);
      });
    });
  }

  /* ---------- Gallery V2: lightbox swipe + pinch + mobile dot indicators ---------- */

  const gallery = section.querySelector('product-gallery');
  if (gallery) {
    // Mobile dot indicators — build from visible figures, update on variant:change.
    let dotsList = gallery.querySelector('[data-gallery-dots]');
    if (!dotsList) {
      dotsList = document.createElement('ul');
      dotsList.className = 'product-gallery__dots';
      dotsList.setAttribute('role', 'list');
      dotsList.setAttribute('data-gallery-dots', '');
      const stage = gallery.querySelector('[data-gallery-stage]');
      if (stage && stage.parentNode) stage.parentNode.insertBefore(dotsList, stage.nextSibling);
    }
    function rebuildDots() {
      const figures = Array.from(gallery.querySelectorAll('.product-gallery__figure')).filter(f => !f.hidden);
      dotsList.innerHTML = '';
      if (figures.length <= 1) return;
      figures.forEach((fig, idx) => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('aria-label', `View image ${idx + 1}`);
        btn.setAttribute('aria-current', fig.hasAttribute('aria-current') ? 'true' : 'false');
        btn.addEventListener('click', () => {
          figures.forEach(f => f.removeAttribute('aria-current'));
          fig.setAttribute('aria-current', 'true');
          fig.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
          dotsList.querySelectorAll('button').forEach((b, i) => b.setAttribute('aria-current', i === idx ? 'true' : 'false'));
        });
        li.appendChild(btn);
        dotsList.appendChild(li);
      });
    }
    rebuildDots();
    document.addEventListener('variant:change', () => setTimeout(rebuildDots, 50));
  }

  // Pinch-to-zoom + swipe in lightbox modal.
  (function initLightboxGestures() {
    const zoom = document.getElementById('ProductZoom');
    if (!zoom) return;
    const stage = zoom.querySelector('[data-zoom-stage]');
    if (!stage) return;
    const next = zoom.querySelector('[data-zoom-next]');
    const prev = zoom.querySelector('[data-zoom-prev]');

    // Swipe to navigate (one-finger horizontal).
    let touchStartX = 0;
    let touchStartY = 0;
    let touchActive = false;
    let touchMoved = false;
    stage.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) { touchActive = false; return; }
      touchActive = true;
      touchMoved = false;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    stage.addEventListener('touchmove', (e) => {
      if (!touchActive || e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - touchStartX;
      const dy = e.touches[0].clientY - touchStartY;
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) touchMoved = true;
    }, { passive: true });
    stage.addEventListener('touchend', (e) => {
      if (!touchActive) return;
      touchActive = false;
      if (!touchMoved) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - touchStartX;
      const dy = t.clientY - touchStartY;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        if (dx < 0) next?.click();
        else prev?.click();
      }
    }, { passive: true });

    // Tap-to-zoom on lightbox images (scale 2x on active image).
    stage.addEventListener('click', (e) => {
      const img = e.target.closest('.product-zoom__image.is-active');
      if (!img) return;
      const isZoomed = img.classList.toggle('is-zoomed');
      if (isZoomed) {
        const rect = img.getBoundingClientRect();
        const ox = ((e.clientX - rect.left) / rect.width) * 100;
        const oy = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${ox}% ${oy}%`;
        img.style.transform = 'scale(2)';
      } else {
        img.style.transform = '';
        img.style.transformOrigin = '';
      }
    });

    // Pinch-to-zoom via touch (two fingers).
    let pinchStartDist = 0;
    let pinchActive = null;
    stage.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 2) return;
      const img = e.target.closest('.product-zoom__image.is-active') || stage.querySelector('.product-zoom__image.is-active');
      if (!img) return;
      pinchActive = img;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchStartDist = Math.hypot(dx, dy);
    }, { passive: true });
    stage.addEventListener('touchmove', (e) => {
      if (!pinchActive || e.touches.length !== 2) return;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const scale = Math.min(4, Math.max(1, dist / Math.max(1, pinchStartDist)));
      pinchActive.style.transform = `scale(${scale})`;
      pinchActive.classList.toggle('is-zoomed', scale > 1.05);
    }, { passive: true });
    stage.addEventListener('touchend', (e) => {
      if (!pinchActive) return;
      if (e.touches.length < 2) {
        // Keep current scale if zoomed in; reset on next non-zoomed tap.
        pinchActive = null;
        pinchStartDist = 0;
      }
    }, { passive: true });
  })();
})();
